import { AxiosError, AxiosResponse } from "axios";
import { IDownloadableFile, IValidationResult } from "lobbie";
import { unescape } from "lodash";
import { createElement, useCallback, useEffect, useRef, useState } from "react";
import PreparingFilesToastMessage from "src/components/shared/PreparingFilesToastMessage";
import { DEFAULT_ERROR_MESSAGE, IS_COMPUTER_WIDTH } from "src/constants";
import { handleError, isFailedRequest, logDev, notify } from "src/utils";
import { useDownloadableFiles } from "./file_downloads";
import { useLoadingRedux } from "./loading/useLoadingRedux";
import { useAxiosGetBlobber, useAxiosSecurePostBlob } from "./useAxios";

interface IOptions {
    mimetype?: string;
    filename: string;
    openInNewTab: boolean;
}

type TCacheable = IDownloadableFile & {
    description: string;
    lastUpdatedOnUnix: number;
    s3ObjectPaths: string[];
    mergedS3ObjectPath?: string;
    zippedS3ObjectPath?: string;
};

const handleFileName = (filename: string, type?: string) => {
    if (!type) {
        return filename;
    }
    if (type.includes("text/csv")) {
        if (filename.endsWith(".csv")) {
            return filename;
        } else {
            return `${filename.split(".").first()}.csv`;
        }
    } else if (type.includes("application/zip")) {
        if (filename.endsWith(".zip")) {
            return filename;
        } else {
            return `${filename.split(".").first()}.zip`;
        }
    } else {
        return filename;
    }
};

export const useDownloadFile = ({
    mimetype,
    filename,
    openInNewTab,
}: IOptions): [
    React.RefObject<HTMLAnchorElement>,
    string | undefined,
    (route: string, data?: Record<string, unknown>) => void,
    boolean,
] => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const getter = useAxiosGetBlobber();
    const poster = useAxiosSecurePostBlob();
    const { addFile } = useDownloadableFiles();
    const [isLoadingRedux, setLoadingRedux] = useLoadingRedux();
    const [file, setFile] = useState<string | undefined>();
    const [isLoading, setLoading] = useState<boolean>(false);

    const setAnchorTag = useCallback(
        (href: string, type?: string) => {
            const a = ref.current;
            if (a) {
                a.download = handleFileName(filename, type);
                if (type || mimetype) {
                    a.type = (type || mimetype) as string;
                }
                a.target = "_blank";
                a.href = href;
                a.rel = "noopener noreferrer";

                logDev("useDownloadFile.setAnchorTag - CLICK ANCHOR");
                a.click();

                logDev("useDownloadFile.setAnchorTag - REVOKE HREF");
                URL.revokeObjectURL(href);
            } else {
                logDev("useDownloadFile.setAnchorTag - NO REF");
            }
        },
        [filename, mimetype],
    );

    const errorHandler = useCallback(
        (error: AxiosError | Error) => {
            logDev(
                `useDownloadFile.getFile - error getting file from route - ${
                    (error as AxiosError)?.request?.url
                }`,
                error,
            );
            setLoading(false);
            setLoadingRedux(false);
            handleError(error, {
                title: "Error downloading file",
                message: DEFAULT_ERROR_MESSAGE,
            });
        },
        [setLoadingRedux],
    );

    const handleJsonBlob = useCallback(
        (blob: Blob) => {
            blob.text()
                .then((text) => {
                    setLoading(false);
                    setLoadingRedux(false);

                    if (!text || typeof text !== "string") {
                        return;
                    }

                    const json = JSON.parse(text) as IValidationResult | IDownloadableFile;
                    if (isFailedRequest(json)) {
                        notify({
                            level: "error",
                            title: "Failed to prepare file.",
                            message: (json as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                        });
                    } else {
                        const cacheable = ((json as IValidationResult).data || json) as TCacheable;
                        addFile(cacheable);

                        notify({
                            level: "default",
                            title: `${
                                cacheable.description
                                    ? `Forms for ${unescape(cacheable.description)}`
                                    : "Your forms"
                            } are being prepared.`,
                            message: createElement(PreparingFilesToastMessage),
                            position: !IS_COMPUTER_WIDTH ? "bottom-center" : undefined,
                        });
                    }
                })
                .catch(errorHandler);
        },
        [addFile, errorHandler, setLoadingRedux],
    );

    const getFile = useCallback(
        async (route: string, data?: Record<string, unknown>) => {
            logDev("useDownloadFile.getFile - getting file");
            setLoading(true);
            setLoadingRedux(true);

            const method = data ? poster(route, data) : getter(route);

            return method
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as string | IValidationResult); // string for blobs
                    if (!result) {
                        setLoading(false);
                        setLoadingRedux(false);
                        notify({
                            level: "error",
                            title: "Error getting file.",
                        });
                        return;
                    }

                    // Create a new blob from the response data - https://stackoverflow.com/a/51252428/6410635
                    let type = response.headers["content-type"] || mimetype;
                    if (type === "application/json" && mimetype === "application/zip") {
                        type = mimetype;
                    }
                    const blob = new Blob([result as string], {
                        type,
                    });

                    // "text" in blob fixes: https://sentry.io/organizations/lobbie/issues/3385245427
                    if (type?.includes("application/json") && "text" in blob) {
                        handleJsonBlob(blob);
                    } else {
                        setLoading(false);
                        setLoadingRedux(false);
                        const href = URL.createObjectURL(blob);

                        if (openInNewTab) {
                            try {
                                const pdf = window.open(href, "_blank");
                                if (!pdf) {
                                    setAnchorTag(href, type);
                                } else {
                                    pdf?.focus();

                                    logDev("useDownloadFile WINDOW OPENED -", !!pdf);
                                    setFile(href);
                                }
                            } catch (error) {
                                logDev(
                                    "useDownloadFile.getFile - ERROR opening object url in new tab. Set file WITHOUT opening window.",
                                );
                                handleError(error as Error, {
                                    sentryMessage:
                                        "useDownloadFile.getFile - Error opening object url in new tab.",
                                });
                                setAnchorTag(href, type);
                            }
                        } else {
                            logDev("useDownloadFile.getFile - Set file WITHOUT opening window");
                            setAnchorTag(href, type);
                        }
                    }
                })
                .catch(errorHandler);
        },
        [
            setLoadingRedux,
            poster,
            getter,
            errorHandler,
            mimetype,
            handleJsonBlob,
            openInNewTab,
            setAnchorTag,
        ],
    );

    useEffect(() => {
        const a = ref.current;
        return () => {
            if (file) {
                logDev("useDownloadFile.useEffect - Remove file on unmount.", file);
                try {
                    if (file) {
                        URL.revokeObjectURL(file);
                    }
                    if (a?.href) {
                        if (a) {
                            a.download = "";
                            a.type = "";
                            a.href = "";
                        }
                    }
                } catch (error) {
                    logDev("useDownloadFile.useEffect - error revoking object url on file");
                    handleError(error as Error, {
                        sentryMessage: "useDownloadFile.useEffect - Error revoking object url.",
                    });
                }
            }
        };
    }, [file, filename]);

    return [ref, file, getFile, isLoadingRedux || isLoading];
};
